// iter150AZ.32 — Read-only PM schedule viewer for a single compressor.
//
// Opened from the "PM" button on each row in
// ``CompressorsOwnedSection``. Reuses the existing
// ``/api/compressors/{serial_id}/service-forecast`` endpoint.
//
// Layout mirrors ``PmItemPickerModal`` but without the multi-select
// + Add-to-Quote flow — this is just informational:
//   * Missing / overdue (rose)
//   * Upcoming PM parts (amber) with next-service hour
//   * Already serviced (slate)
// Plus a compact "Repetitive every N hr" badge so reps know which
// rows recur.
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X as XIcon, Spinner, WarningCircle, Wrench, CheckCircle, Repeat } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../lib/api";
import { useT } from "../lib/i18n";
import { useCompressorServiceForecast } from "../lib/queryHooks";
import { useQuery } from "@tanstack/react-query";
import PmViewerTour, { TourHelpButton } from "./PmViewerTour";

function _classifyBucket(item) {
  if (item.bucket === "due_now") return "missing";
  if (item.bucket === "next_service" || item.bucket === "future") return "service";
  return "other";
}

function _hourSummary(item, currentHours, t) {
  const hoursUntil = item.hours_until;
  const nextAt = item.next_service_at_hour;
  if (hoursUntil == null) return t("pm_viewer.no_interval", "No hour-based interval");
  if (hoursUntil <= 0) {
    const overBy = (currentHours ?? 0) - (nextAt ?? 0);
    if (overBy > 0) return t("pm_viewer.overdue_by", "Overdue by {{n}} hr", { n: overBy });
    return t("pm_viewer.due_now", "Due now @ {{n}} hr", { n: nextAt });
  }
  return t("pm_viewer.due_in", "Due in {{n}} hr (at {{at}})",
           { n: hoursUntil, at: nextAt });
}

function _isRepetitive(item) {
  // ``Service_Table_by_SN.Repetitive`` lands on the row as
  // ``item.repetitive`` (str) on most cases — be tolerant.
  const v = item.repetitive;
  if (v == null) return false;
  return String(v).toLowerCase().startsWith("rep");
}

function PmRow({ item, group, currentHours }) {
  const t = useT();
  const ringClass =
    group === "missing" ? "border-rose-200 bg-rose-50/40"
    : group === "service" ? "border-amber-200 bg-amber-50/40"
    : "border-slate-200 bg-slate-50/40";
  const badgeClass =
    group === "missing" ? "bg-rose-600 text-white"
    : group === "service" ? "bg-amber-500 text-white"
    : "bg-slate-400 text-white";
  const badgeLabel =
    group === "missing" ? t("pm_viewer.badge_missing", "MISSING")
    : group === "service" ? t("pm_viewer.badge_pm", "PM")
    : t("pm_viewer.badge_ok", "OK");
  const qty = Number(item.units_short || 0) > 0
    ? Number(item.units_short)
    : Number(item.qty_per_service || 1) || 1;
  const interval = Number(item.interval_hours || 0);
  return (
    <div className={`px-3 py-2 border ${ringClass}`}
         data-testid={`pm-viewer-part-${item.product_code || item.id}`}>
      <div className="flex items-center gap-2 mb-0.5">
        <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-sm uppercase tracking-wider ${badgeClass}`}>
          {badgeLabel}
        </span>
        <span className="text-sm font-semibold text-slate-900 truncate">
          {item.part_name || item.service_type || "—"}
        </span>
        {_isRepetitive(item) && interval > 0 && (
          <span className="text-[9px] font-bold uppercase tracking-wider px-1 py-0.5 rounded-sm bg-blue-100 text-blue-800 inline-flex items-center gap-0.5">
            <Repeat size={9} weight="bold" />
            {t("pm_viewer.repetitive", "every {{n}}h", { n: interval })}
          </span>
        )}
      </div>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 text-[10px] text-slate-600 font-mono">
        {item.product_code && <span>{item.product_code}</span>}
        <span className="font-sans">
          {t("pm_viewer.qty_label", "Qty per service")}: <strong>{qty}</strong>
        </span>
        <span className={`font-sans ${group === "missing" ? "text-rose-700 font-bold" : "text-slate-600"}`}>
          {_hourSummary(item, currentHours, t)}
        </span>
        {Number(item.units_purchased || 0) > 0 && (
          <span className="font-sans text-slate-500">
            {t("pm_viewer.purchased", "purchased so far")}: <strong>{Number(item.units_purchased)}</strong>
          </span>
        )}
      </div>
      {item.description && (
        <div className="text-[10px] text-slate-500 truncate mt-0.5 italic">
          {item.description}
        </div>
      )}
    </div>
  );
}

/**
 * iter150AZ.37 — Service Parts Chart (PM × hours pivot).
 *
 * Left-most column = PM part name + ``(repetitive)`` /
 * ``(non-repetitive)`` label. Top headers = service hour intervals.
 * Cells = invoice number where that part was purchased, or "—" when
 * not yet purchased.
 */
function PmPivotChart({ data, onClose, serialId }) {
  const t = useT();
  const navigate = useNavigate();
  const columns = data?.columns || [];
  const items = data?.items || [];
  // iter150AZ.41 — server-side roll-up of how many cells at each
  // hour-column are still MISSING (not in stock, not purchased).
  // The "+ Quote" affordance is hidden on columns where every part
  // is already fulfilled.
  const missingByHour = data?.missing_by_hour || {};
  // iter150AZ.39 — generating a quote for an entire hour-interval
  // runs through ``POST /compressors/{id}/pm-pivot/quote`` (backend
  // selects every PM part due at that hour). ``busyHour`` blocks the
  // button while the request is in flight to prevent double-clicks.
  const [busyHour, setBusyHour] = useState(null);
  const [genError, setGenError] = useState("");
  // iter150AZ.38 — clicking a purchased cell deep-links to the source
  // quote/invoice. Close the modal first so the user lands on the
  // quote detail page cleanly. ``heading_id`` is the
  // QuoteInvoiceHeadingID — same id the rest of the app uses.
  const openInvoice = (headingId) => {
    if (!headingId) return;
    onClose && onClose();
    // setTimeout lets the modal's close animation start before we
    // change routes; without this the navigate fires while React is
    // unmounting the modal and feels janky.
    setTimeout(() => navigate(`/quote/${encodeURIComponent(headingId)}`), 50);
  };
  const generateQuote = async (atHour, qType = "PM") => {
    if (!serialId || !atHour || busyHour) return;
    setBusyHour(atHour);
    setGenError("");
    try {
      const r = await api.post(
        `/compressors/${encodeURIComponent(serialId)}/pm-pivot/quote`,
        { at_hour: atHour, q_type: qType },
      );
      const headingId = r?.data?.heading_id;
      if (!headingId) throw new Error("No heading_id returned");
      onClose && onClose();
      setTimeout(() => navigate(`/quote/${encodeURIComponent(headingId)}`), 50);
    } catch (e) {
      setGenError(
        e?.response?.data?.detail
        || e?.message
        || "Failed to generate quote",
      );
    } finally {
      setBusyHour(null);
    }
  };
  if (!items.length || !columns.length) {
    return (
      <div className="text-[12px] text-slate-600 py-6 text-center"
           data-testid="pm-pivot-empty">
        {t("pm_viewer.pivot_empty",
           "No PM intervals are defined for this compressor.")}
      </div>
    );
  }
  return (
    <div className="overflow-x-auto" data-testid="pm-pivot-chart">
      {genError && (
        <div className="mb-2 px-3 py-2 bg-rose-50 border border-rose-200 text-rose-800 text-[11px] rounded-sm flex items-center gap-2"
             data-testid="pm-pivot-gen-error">
          <WarningCircle size={14} weight="bold" />
          <span>{genError}</span>
        </div>
      )}
      <table className="min-w-full text-[10px] border border-slate-200">
        <thead className="bg-slate-50">
          <tr>
            <th className="sticky left-0 z-10 bg-slate-50 text-left text-[9px] uppercase tracking-wider text-slate-600 px-2 py-1.5 border-b border-r border-slate-200 min-w-[220px]">
              {t("pm_viewer.pivot_part", "PM Service Part")}
            </th>
            {/* iter150AZ.40 — Stock column shows the on-hand count
                from Service_Table_by_SN.Stock. Stock is consumed
                first (left-most intervals) before purchases fill the
                remaining cells. */}
            <th className="sticky left-[220px] z-10 bg-slate-50 text-center text-[9px] uppercase tracking-wider text-slate-600 px-2 py-1.5 border-b border-r border-slate-200 min-w-[60px]"
                data-tour="pm-pivot-stock">
              {t("pm_viewer.pivot_stock", "Stock")}
            </th>
            {columns.map((col) => {
              const busy = busyHour === col;
              const hasMissing = Number(missingByHour[col] || 0) > 0;
              return (
                <th key={col}
                    data-testid={`pm-pivot-col-${col}`}
                    className="text-center font-mono font-bold text-slate-700 px-2 py-1.5 border-b border-r last:border-r-0 border-slate-200 whitespace-nowrap min-w-[120px]">
                  <div className="flex flex-col items-center gap-1">
                    <span>{col.toLocaleString()} hr</span>
                    {/* iter150AZ.39 — per-column "Generate quote"
                        affordance. iter150AZ.41 — only rendered for
                        columns with at least one MISSING part; the
                        generated quote includes only the missing
                        parts at that interval. */}
                    {hasMissing && (
                      <button
                        type="button"
                        onClick={() => generateQuote(col, "PM")}
                        disabled={busy}
                        data-testid={`pm-pivot-gen-${col}`}
                        data-tour="pm-pivot-quote-btn"
                        title={t("pm_viewer.pivot_gen_quote_tip",
                                 "Generate a PM quote for the missing parts at {n} hr", { n: col })}
                        className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm border transition ${busy ? "bg-slate-200 text-slate-400 border-slate-200 cursor-wait" : "bg-white text-emerald-700 border-emerald-200 hover:bg-emerald-50"}`}
                      >
                        {busy ? "…" : "+ Quote"}
                      </button>
                    )}
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {items.map((row) => {
            // Map ``at_hour -> cell`` so we can render in column order.
            const cellByHour = new Map();
            (row.cells || []).forEach((c) => cellByHour.set(c.at_hour, c));
            return (
              <tr key={row.id || row.kit_computed_key}
                  data-testid={`pm-pivot-row-${row.kit_computed_key || row.id}`}
                  className="even:bg-slate-50/40">
                <td className="sticky left-0 z-10 bg-inherit text-left px-2 py-1.5 border-b border-r border-slate-200 min-w-[220px]">
                  <div className="font-semibold text-slate-800 truncate">{row.part_name}</div>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <span className={`text-[8px] font-bold uppercase tracking-wider px-1 py-0.5 rounded-sm border ${row.repetitive ? "bg-blue-50 text-blue-800 border-blue-200" : "bg-slate-100 text-slate-700 border-slate-200"}`}>
                      {row.repetitive
                        ? t("pm_viewer.pivot_repetitive", "repetitive")
                        : t("pm_viewer.pivot_nonrepetitive", "non-repetitive")}
                    </span>
                    <span className="text-[9px] text-slate-500 font-mono">
                      every {Number(row.interval_hours).toLocaleString()} hr · qty {row.qty_per_service}
                    </span>
                  </div>
                </td>
                {/* iter150AZ.40 — Stock count from Service_Table_by_SN */}
                <td className="sticky left-[220px] z-10 bg-inherit text-center font-mono px-2 py-1.5 border-b border-r border-slate-200 min-w-[60px]"
                    data-testid={`pm-pivot-stock-${row.kit_computed_key || row.id}`}
                    title={Number(row.stock) > 0
                      ? `On-hand: ${row.stock} unit(s). Covers ${Math.floor(Number(row.stock) / Math.max(1, row.qty_per_service))} service(s) before purchases are consumed.`
                      : "No stock on hand."}>
                  <span className={Number(row.stock) > 0 ? "text-emerald-700 font-semibold" : "text-slate-300"}>
                    {Number(row.stock) > 0 ? Number(row.stock).toLocaleString() : "—"}
                  </span>
                </td>
                {columns.map((col) => {
                  const cell = cellByHour.get(col);
                  // Cell is "blank" (—) when the part doesn't include
                  // this column at all (e.g. non-repetitive break-in
                  // oil only owns the 500 hr column).
                  const owned = !!cell;
                  const fromStock = owned && cell.from_stock;
                  const purchased = owned && cell.invoice_number;
                  const missing = owned && cell.missing;
                  const clickable = purchased && cell.heading_id;
                  // Format the invoice date alongside the invoice
                  // number — user request iter150AZ.40.
                  const fmtDate = (iso) => {
                    if (!iso) return "";
                    try {
                      const d = new Date(iso);
                      if (Number.isNaN(d.getTime())) return "";
                      return d.toLocaleDateString("en-US",
                        { month: "short", day: "numeric", year: "2-digit" });
                    } catch { return ""; }
                  };
                  const dateStr = purchased ? fmtDate(cell.invoice_date) : "";
                  let body;
                  if (!owned) {
                    body = <span>—</span>;
                  } else if (fromStock) {
                    body = <span className="text-amber-700 font-semibold text-[9px] uppercase tracking-wider">STOCK</span>;
                  } else if (purchased) {
                    body = (
                      <span className="flex flex-col items-center leading-tight">
                        <span>#{cell.invoice_number}</span>
                        {dateStr && <span className="text-[8px] text-emerald-700/70">{dateStr}</span>}
                      </span>
                    );
                  } else if (missing) {
                    // iter150AZ.41 — red "MISSING" badge replaces the
                    // muted "·" placeholder so reps immediately see
                    // which intervals still need a quote.
                    body = (
                      <span
                        data-testid={`pm-pivot-cell-missing-${row.kit_computed_key || row.id}-${col}`}
                        data-tour="pm-pivot-missing"
                        className="text-rose-700 font-bold text-[9px] uppercase tracking-wider"
                      >
                        {t("pm_viewer.pivot_missing", "missing")}
                      </span>
                    );
                  } else {
                    body = <span>·</span>;
                  }
                  const cellClass = `text-center font-mono px-2 py-1.5 border-b border-r last:border-r-0 border-slate-200 ${owned ? (fromStock ? "bg-amber-50 text-amber-800" : purchased ? "text-emerald-800 font-semibold" : missing ? "bg-rose-50 text-rose-800" : "text-slate-300") : "bg-slate-100/60 text-slate-300"}`;
                  const title = !owned ? "—"
                    : fromStock ? "Fulfilled from on-hand stock"
                    : purchased ? `${clickable ? "Open " : ""}Invoice #${cell.invoice_number}${cell.invoice_date ? " · " + cell.invoice_date : ""}`
                    : missing ? "Missing — not yet purchased or in stock"
                    : "Not yet purchased";
                  return clickable ? (
                    <td key={col}
                        className={`${cellClass} p-0`}
                        data-testid={`pm-pivot-cell-${row.kit_computed_key || row.id}-${col}`}>
                      <button
                        type="button"
                        onClick={() => openInvoice(cell.heading_id)}
                        title={title}
                        data-testid={`pm-pivot-cell-link-${row.kit_computed_key || row.id}-${col}`}
                        className="w-full h-full px-2 py-1.5 hover:bg-emerald-50 hover:underline focus:outline-none focus:ring-1 focus:ring-emerald-400"
                      >
                        {body}
                      </button>
                    </td>
                  ) : (
                    <td key={col}
                        data-testid={`pm-pivot-cell-${row.kit_computed_key || row.id}-${col}`}
                        className={cellClass}
                        title={title}
                    >
                      {body}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="text-[9px] text-slate-400 italic mt-2" data-testid="pm-pivot-legend">
        {t("pm_viewer.pivot_legend",
           "Stock column shows on-hand inventory that fulfils the first cells (amber \"STOCK\"). #INV cells are clickable and open the source invoice. Red \"MISSING\" cells are parts not yet purchased or in stock. The \"+ Quote\" button appears only on hour-columns with at least one MISSING part and generates a quote for only those missing parts. \"—\" = column doesn't apply.")}
      </p>
    </div>
  );
}

export default function CompressorPmViewerModal({
  open, onClose, serialId, serialNumber, productName,
}) {
  const t = useT();
  // iter150AZ.37 — view toggle: "schedule" (existing buckets) or
  // "pivot" (new PM service parts × hours chart).
  const [view, setView] = useState("schedule");
  // iter150AZ.33 — Cached via react-query. The same compressor's PM
  // forecast is shared across this modal AND PmItemPickerModal — so
  // opening one immediately after the other costs zero extra GETs.
  const { data: forecast, isLoading: loading, error: queryError } =
    useCompressorServiceForecast(serialId, { enabled: !!open });
  const [error, setError] = useState("");
  useEffect(() => {
    setError(queryError ? (queryError?.response?.data?.detail
                            || "Could not load PM schedule") : "");
  }, [queryError]);

  // iter150AZ.37 — fetch the pivot data lazily only when the user
  // switches to the pivot tab.
  const { data: pivot, isLoading: pivotLoading } = useQuery({
    queryKey: ["compressor-pm-pivot", serialId],
    queryFn: async () => {
      const r = await api.get(
        `/compressors/${encodeURIComponent(serialId)}/pm-pivot`,
      );
      return r.data;
    },
    enabled: !!serialId && !!open && view === "pivot",
  });

  const groups = useMemo(() => {
    const out = { missing: [], service: [], other: [] };
    for (const it of (forecast?.items || [])) {
      out[_classifyBucket(it)].push(it);
    }
    out.missing.sort((a, b) =>
      Number(b.units_short || 0) - Number(a.units_short || 0)
      || Number(a.hours_until ?? 9e9) - Number(b.hours_until ?? 9e9),
    );
    out.service.sort((a, b) =>
      Number(a.hours_until ?? 9e9) - Number(b.hours_until ?? 9e9),
    );
    return out;
  }, [forecast]);

  // iter150AZ.42 — guided tour state. Auto-runs on first modal open
  // via localStorage; the Help button increments tourTick to force a
  // manual re-launch.
  const [tourTick, setTourTick] = useState(0);

  // sg_36900e60da5347 — Authenticated PDF download.
  // The previous implementation was a plain ``<a href>`` that opened
  // the PDF endpoint in a new tab. New tabs don't forward the
  // ``X-User-Email`` header CAP uses for auth, so the backend
  // returned 403 and the user saw an empty/error page instead of a
  // PDF. Fix: fetch the PDF via the authenticated ``api`` client
  // (which attaches the header) into a Blob, then trigger the
  // download client-side. Same pattern as CommissioningHistoryPage
  // and DealerAgreementsPage.
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const handleDownloadPdf = async () => {
    if (!serialId || downloadingPdf) return;
    setDownloadingPdf(true);
    try {
      const r = await api.get(
        `/compressors/${encodeURIComponent(serialId)}/pm-pivot/pdf`,
        { responseType: "blob" },
      );
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      // Match the backend's Content-Disposition filename pattern so
      // saved PDFs are easy to find on disk.
      const sn = (serialNumber || serialId || "compressor").toString()
        .replace(/[^A-Za-z0-9_-]+/g, "_");
      a.download = `pm_service_parts_chart_${sn}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      // Surface the real reason (auth, missing compressor, DB outage)
      // instead of failing silently like the legacy <a href>.
      const detail = e?.response?.data?.detail
                  || e?.response?.statusText
                  || e?.message
                  || "Could not download PDF";
      toast.error(`PDF download failed — ${detail}`);
    } finally {
      setDownloadingPdf(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
         data-testid="pm-viewer-modal">
      <PmViewerTour runFlag={tourTick} />
      <div className="bg-white w-full max-w-5xl max-h-[90vh] rounded-sm shadow-2xl flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200"
             data-tour="pm-header">
          <div>
            <h2 className="text-sm font-bold uppercase tracking-wider text-slate-900">
              {t("pm_viewer.title", "PM Schedule")}
            </h2>
            <p className="text-[11px] text-slate-500">
              {serialNumber ? (
                <span className="font-mono font-bold">SN {serialNumber}</span>
              ) : (
                <span className="text-slate-400 italic">No serial number</span>
              )}
              {productName && <span className="ml-1">· {productName}</span>}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* sg_36900e60da5347 — Authenticated PDF download.
                The legacy ``<a href>`` opened in a new tab without the
                X-User-Email header, so the backend rejected the
                request with 403. The button below fetches the PDF
                through the authenticated axios client. */}
            {serialId && (
              <button
                type="button"
                onClick={handleDownloadPdf}
                disabled={downloadingPdf}
                data-testid="pm-viewer-pdf-download"
                className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm border border-emerald-300 text-emerald-700 hover:bg-emerald-50 disabled:opacity-60 disabled:cursor-wait"
                title={t("pm_viewer.pdf_tip",
                         "Download the PM schedule + service parts chart + a copy of each linked invoice as one PDF")}
              >
                {downloadingPdf ? (
                  <>
                    <Spinner size={11} weight="bold" className="animate-spin" />
                    {t("pm_viewer.pdf_downloading", "Preparing PDF…")}
                  </>
                ) : (
                  <>↓ PDF</>
                )}
              </button>
            )}
            <button type="button" onClick={onClose}
                    data-testid="pm-viewer-close"
                    className="text-slate-400 hover:text-slate-700">
              <XIcon size={18} weight="bold" />
            </button>
          </div>
        </div>

        {/* iter150AZ.42 — tour Help affordance, sits under the header
            so it doesn't crowd the close X. */}
        <div className="flex items-center justify-end px-5 pt-2">
          <TourHelpButton
            scope="pm_viewer"
            onClick={() => setTourTick((n) => n + 1)}
            testid="pm-viewer-help-button"
          />
        </div>

        {/* iter150AZ.37 — view-mode tabs */}
        <div className="flex items-center gap-1 px-5 pt-3" data-testid="pm-viewer-tabs" data-tour="pm-tabs">
          <button
            type="button"
            onClick={() => setView("schedule")}
            data-testid="pm-viewer-tab-schedule"
            className={`text-[10px] uppercase tracking-wider font-bold px-3 py-1.5 rounded-sm border ${view === "schedule" ? "bg-slate-700 text-white border-slate-700" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}
          >
            Schedule
          </button>
          <button
            type="button"
            onClick={() => setView("pivot")}
            data-testid="pm-viewer-tab-pivot"
            className={`text-[10px] uppercase tracking-wider font-bold px-3 py-1.5 rounded-sm border ${view === "pivot" ? "bg-slate-700 text-white border-slate-700" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}
          >
            Service Parts Chart
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-3">
          {error && (
            <div className="mb-3 px-3 py-2 bg-rose-50 border border-rose-200 text-rose-800 text-[11px] rounded-sm flex items-start gap-2"
                 data-testid="pm-viewer-error">
              <WarningCircle size={14} weight="bold" className="flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {view === "schedule" && loading && (
            <div className="text-[12px] text-slate-500 inline-flex items-center gap-2 py-6">
              <Spinner size={13} weight="bold" className="animate-spin" />
              {t("pm_viewer.loading", "Loading PM schedule…")}
            </div>
          )}

          {view === "schedule" && !loading && !error && forecast && (
            <>
              {/* Summary banner */}
              <div className="mb-3 px-3 py-2 rounded-sm bg-slate-50 border border-slate-200
                              flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-slate-700"
                   data-testid="pm-viewer-summary">
                <div>
                  <span className="text-slate-400 uppercase tracking-wider text-[9px]">
                    {t("pm_viewer.run_hours", "Hours")}:
                  </span>
                  <span className="ml-1 font-mono font-bold text-slate-900">
                    {forecast.summary?.current_hours ?? "—"}
                  </span>
                </div>
                {forecast.summary?.next_service_at_hour && (
                  <div>
                    <span className="text-slate-400 uppercase tracking-wider text-[9px]">
                      {t("pm_viewer.next_service_at", "Next PM at")}:
                    </span>
                    <span className="ml-1 font-mono font-bold text-slate-900">
                      {forecast.summary.next_service_at_hour}h
                    </span>
                    <span className="ml-1 text-slate-500">
                      ({t("pm_viewer.in_hrs", "in {{n}}h", { n: forecast.summary.hours_until_next })})
                    </span>
                  </div>
                )}
                {forecast.summary?.due_now_count > 0 && (
                  <div>
                    <span className="text-rose-600 font-bold">
                      {forecast.summary.due_now_count} {t("pm_viewer.missing_count", "missing/overdue")}
                    </span>
                  </div>
                )}
              </div>

              {/* Groups */}
              {["missing", "service", "other"].map((g) => {
                const list = groups[g];
                if (!list || list.length === 0) return null;
                const title =
                  g === "missing" ? t("pm_viewer.group_missing", "Missing / Overdue")
                  : g === "service" ? t("pm_viewer.group_service", "Upcoming PM parts")
                  : t("pm_viewer.group_other", "Other (already serviced)");
                const Icon = g === "missing" ? WarningCircle : g === "service" ? Wrench : CheckCircle;
                const titleClass =
                  g === "missing" ? "text-rose-900"
                  : g === "service" ? "text-amber-900"
                  : "text-slate-600";
                return (
                  <div key={g} className="mb-3" data-testid={`pm-viewer-group-${g}`}>
                    <h3 className={`flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider ${titleClass} mb-1.5`}>
                      <Icon size={13} weight="bold" />
                      {title}
                      <span className="text-slate-400 font-normal">({list.length})</span>
                    </h3>
                    <div className="space-y-1">
                      {list.map(it => (
                        <PmRow key={it.id || it.product_code}
                               item={it}
                               group={g}
                               currentHours={forecast.summary?.current_hours} />
                      ))}
                    </div>
                  </div>
                );
              })}

              {groups.missing.length === 0 && groups.service.length === 0 && groups.other.length === 0 && (
                <div className="text-[12px] text-slate-600 py-6 text-center"
                     data-testid="pm-viewer-empty">
                  {t("pm_viewer.no_pm_schedule",
                     "No PM schedule is registered for this compressor.")}
                </div>
              )}
            </>
          )}

          {/* iter150AZ.37 — Service Parts Chart (pivot view) */}
          {view === "pivot" && pivotLoading && (
            <div className="text-[12px] text-slate-500 inline-flex items-center gap-2 py-6">
              <Spinner size={13} weight="bold" className="animate-spin" />
              {t("pm_viewer.loading_pivot", "Building service-parts chart…")}
            </div>
          )}
          {view === "pivot" && !pivotLoading && pivot && (
            <PmPivotChart data={pivot} onClose={onClose} serialId={serialId} />
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-200 bg-slate-50/60 flex items-center justify-end">
          <button type="button"
                  onClick={onClose}
                  data-testid="pm-viewer-done"
                  className="h-9 px-4 text-[10px] uppercase font-bold tracking-wider rounded-sm
                             bg-slate-700 hover:bg-slate-800 text-white">
            {t("action.close", "Close")}
          </button>
        </div>

      </div>
    </div>
  );
}
